import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';

type UserProfile = { 
  id: string; 
  email: string; 
  role: 'admin' | 'embaixador'; 
  must_set_password?: boolean;
  display_name?: string | null;
  phone?: string | null;
};

export default function useAuth() {
  const [session, setSession] = useState<Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session']>(null);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchUserProfile = useCallback(async (userId: string) => {
    // Sem Promise.race/timeout: buscar por ID e garantir single()
    const { data, error } = await supabase
      .from('users')
      .select('id,email,role,must_set_password,display_name,phone')
      .eq('id', userId)
      .single();

    if (error) throw error;
    return data as UserProfile;
  }, []);

  // Inicialização única
  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!mounted) return;
      setSession(session ?? null);

      if (session?.user) {
        try {
          const profile = await fetchUserProfile(session.user.id);
          if (!mounted) return;
          setUser(profile);
          setIsAdmin(profile.role === 'admin');
        } catch (e) {
          console.error('Erro ao buscar perfil inicial:', e);
          if (!mounted) return;
          setUser(null);
          setIsAdmin(false);
        }
      } else {
        setUser(null);
        setIsAdmin(false);
      }
      setLoading(false);
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s ?? null);
      if (s?.user) {
        fetchUserProfile(s.user.id)
          .then((p) => {
            setUser(p);
            setIsAdmin(p.role === 'admin');
          })
          .catch((e) => {
            console.error('Erro ao buscar perfil após mudança de auth:', e);
            setUser(null);
            setIsAdmin(false);
          });
      } else {
        setUser(null);
        setIsAdmin(false);
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [fetchUserProfile]);

  // Login seguro: só chama signIn se NÃO houver sessão
  const signIn = useCallback(async (email: string, password: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      console.log('Já existe sessão, não chamarei signIn novamente.');
      return { data: { session }, error: null as any };
    }
    return await supabase.auth.signInWithPassword({ email, password });
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
    setIsAdmin(false);
    setSession(null);
  }, []);

  return useMemo(() => ({
    session, user, isAdmin, loading, signIn, signOut, supabase
  }), [session, user, isAdmin, loading, signIn, signOut]);
}